/**
 * Toast Component
 * Notification toast with design system tokens and semantic variants
 */

import React from "react";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  Box,
  VStack,
  AlertProps as ChakraAlertProps,
} from "@chakra-ui/react";
import { useColorModeValue } from "design-system/hooks/useColorModeValue";
import { resolveSpacing } from "design-system/foundation";
import CDS from "design-system";

// Branded types for Toast variants and positions
declare const __toast_variant_brand: unique symbol;
declare const __toast_position_brand: unique symbol;

export interface ToastVariantToken {
  readonly [__toast_variant_brand]: true;
  readonly _value: string;
}

export interface ToastPositionToken {
  readonly [__toast_position_brand]: true;
  readonly _value: string;
}

// Factory functions
const createToastVariantToken = (value: string): ToastVariantToken => ({ _value: value } as ToastVariantToken);

const createToastPositionToken = (value: string): ToastPositionToken => ({ _value: value } as ToastPositionToken);

export interface ToastProps extends Omit<ChakraAlertProps, "status" | "variant"> {
  title?: string;
  description?: string;
  variant?: ToastVariantToken;
  position?: ToastPositionToken;
  isClosable?: boolean;
  onClose?: () => void;
  duration?: number; // in milliseconds
}

function ToastComponent({
  title,
  description,
  variant = toastTokens.variant.info,
  position = toastTokens.position.topRight,
  isClosable = true,
  onClose,
  duration = 5000,
  children,
  ...props
}: ToastProps) {
  // Use color mode reactive colors
  const successBg = useColorModeValue(CDS.Color.surface.success);
  const successColor = useColorModeValue(CDS.Color.text.success);
  const successBorder = useColorModeValue(CDS.Color.border.success);

  const warningBg = useColorModeValue(CDS.Color.surface.warning);
  const warningColor = useColorModeValue(CDS.Color.text.warning);
  const warningBorder = useColorModeValue(CDS.Color.border.warning);

  const errorBg = useColorModeValue(CDS.Color.surface.error);
  const errorColor = useColorModeValue(CDS.Color.text.error);
  const errorBorder = useColorModeValue(CDS.Color.border.error);

  const infoBg = useColorModeValue(CDS.Color.primitive.brand(100));
  const infoColor = useColorModeValue(CDS.Color.primitive.brand(800));
  const infoBorder = useColorModeValue(CDS.Color.primitive.brand(200));

  // Auto-close functionality
  React.useEffect(() => {
    if (duration > 0 && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const getVariantStyles = (variant: ToastVariantToken) => {
    switch (variant._value) {
      case "success":
        return {
          bg: successBg,
          color: successColor,
          borderColor: successBorder,
          status: "success" as const,
        };
      case "warning":
        return {
          bg: warningBg,
          color: warningColor,
          borderColor: warningBorder,
          status: "warning" as const,
        };
      case "error":
        return {
          bg: errorBg,
          color: errorColor,
          borderColor: errorBorder,
          status: "error" as const,
        };
      case "info":
      default:
        return {
          bg: infoBg,
          color: infoColor,
          borderColor: infoBorder,
          status: "info" as const,
        };
    }
  };

  const getPositionStyles = (position: ToastPositionToken) => {
    const spacing = resolveSpacing(CDS.Spacing.medium);

    switch (position._value) {
      case "topLeft":
        return { position: "fixed" as const, top: spacing, left: spacing };
      case "topCenter":
        return { position: "fixed" as const, top: spacing, left: "50%", transform: "translateX(-50%)" };
      case "topRight":
        return { position: "fixed" as const, top: spacing, right: spacing };
      case "bottomLeft":
        return { position: "fixed" as const, bottom: spacing, left: spacing };
      case "bottomCenter":
        return { position: "fixed" as const, bottom: spacing, left: "50%", transform: "translateX(-50%)" };
      case "bottomRight":
        return { position: "fixed" as const, bottom: spacing, right: spacing };
      default:
        return { position: "fixed" as const, top: spacing, right: spacing };
    }
  };

  const variantStyles = getVariantStyles(variant);
  const positionStyles = getPositionStyles(position);

  return (
    <Box
      {...positionStyles}
      zIndex={9999}
      maxW="400px"
      minW="300px"
    >
      <Alert
        status={variantStyles.status}
        variant="left-accent"
        border="1px solid"
        borderRadius={resolveSpacing(CDS.Spacing.small)} // 8px
        boxShadow="lg"
        {...variantStyles}
        {...props}
      >
        <AlertIcon />
        <VStack align="start" spacing={1} flex={1}>
          {title && (
            <AlertTitle fontSize="sm" fontWeight="semibold">
              {title}
            </AlertTitle>
          )}
          {description && (
            <AlertDescription fontSize="sm">
              {description}
            </AlertDescription>
          )}
          {children}
        </VStack>
        {isClosable && (
          <CloseButton
            onClick={onClose}
            size="sm"
            position="absolute"
            top={resolveSpacing(CDS.Spacing.small)}
            right={resolveSpacing(CDS.Spacing.small)}
          />
        )}
      </Alert>
    </Box>
  );
}

// Toast tokens
const toastTokens = {
  variant: {
    success: createToastVariantToken("success"),
    warning: createToastVariantToken("warning"),
    error: createToastVariantToken("error"),
    info: createToastVariantToken("info"),
  } as const,

  position: {
    topLeft: createToastPositionToken("topLeft"),
    topCenter: createToastPositionToken("topCenter"),
    topRight: createToastPositionToken("topRight"),
    bottomLeft: createToastPositionToken("bottomLeft"),
    bottomCenter: createToastPositionToken("bottomCenter"),
    bottomRight: createToastPositionToken("bottomRight"),
  } as const,
} as const;

// Public API - combines tokens and component
export const Toast = Object.assign(ToastComponent, toastTokens);
